<template>
  <section>
    <coozzy-card class="global-card no-top-border table-card">
      <!-- Heading -->
      <div class="row justify-content-between align-items-center">
        <div class="col-12">
          <div class="row">
            <div class="col-md-8">
              <h5 v-if="selectedView === 'componentsCostsList'">
                {{ componentsCount }} {{ $t('message.onBoarding.investmentPlanning.components') }}
              </h5>
              <h5 v-else-if="selectedView === 'planningCostsList'">
                {{ plansCount }} {{ $t('message.onBoarding.investmentPlanning.planningCostsTitle') }}
              </h5>
            </div>
            <template
              v-if="selectedView === 'componentsCostsList'">
              <div
                class="col-12 col-md-2">
                <coozzy-button
                  v-if="isFiltered"
                  class="w-100 sort"
                  @click="initFilterComponents()">
                  <coozzy-filter-icon />
                  {{ $t('message.generic.clearFilters') }}
                </coozzy-button>
              </div>
              <div
                v-if="hasInvestmentPlanRole"
                class="col-12 col-md-2">
                <coozzy-button
                  design="prop-green"
                  class="w-100 sort"
                  @click="readOnly ? goToEditComponent() : createComponentClicked()">
                  {{ $t('message.onBoarding.investmentPlanning.addComponent') }}
                </coozzy-button>
              </div>
            </template>
            <template
              v-else-if="selectedView === 'planningCostsList'">
              <div
                class="col-12 col-md-2">
                <coozzy-button
                  v-if="isFiltered"
                  class="w-100 sort"
                  @click="initFilterPlans()">
                  <coozzy-filter-icon />
                  {{ $t('message.generic.clearFilters') }}
                </coozzy-button>
              </div>
              <div
                v-if="hasInvestmentPlanRole"
                class="col-12 col-md-2">
                <coozzy-button
                  design="prop-green"
                  class="w-100 sort"
                  @click="readOnly ? goToEditComponent() : createComponentClicked()">
                  {{ $t('message.onBoarding.investmentPlanning.addPlanningCosts') }}
                </coozzy-button>
              </div>
            </template>
          </div>
        </div>
        <div class="col-12">
          <div class="col-12 p-0">
            <div class="row mb-2 mx-0">
              <div class="col-2 pl-0">
                <coozzy-form-radio
                  v-model="selectedView"
                  name="investmentPlanning"
                  class="p-0 btn likeBtn"
                  :default-model="selectedView"
                  :select-value="'componentsCostsList'">
                  {{ $t('message.onBoarding.investmentPlanning.componentCostsTitle') }}
                </coozzy-form-radio>
              </div>
              <div
                class="col-2 pl-0 pr-0">
                <coozzy-form-radio
                  v-model="selectedView"
                  name="investmentPlanning"
                  class="p-0 btn likeBtn"
                  :default-model="selectedView"
                  :select-value="'planningCostsList'">
                  {{ $t('message.onBoarding.investmentPlanning.planningCostsTitle') }}
                </coozzy-form-radio>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-if="selectedView === 'componentsCostsList'">
        <component-costs
          ref="propertyComponentCosts"
          :property="property"
          :read-only="readOnly"
          :components="listComponents"
          :buildings-list="buildingsList"
          :objects-list="objectsList"
          source="property"
          :filters="filters"
          @filters-changed="filtersChanged"
          @change-component="emitChangesComponents"
          @component-added="emitAddComponents"
          @delete-component="deleteComponent"
          @delete-new-component="deleteNewComponent"
          @save-data="saveData" />
      </template>
      <template v-else-if="selectedView === 'planningCostsList'">
        <planning-costs
          ref="propertyPlanningCosts"
          :property="property"
          :read-only="readOnly"
          :list-planning-costs="listPlanningCosts"
          :buildings-list="buildingsList"
          :objects-list="objectsList"
          source="property"
          :filters="filters"
          @filters-changed="filtersChanged"
          @change-plan="emitChangesPlans"
          @plan-added="emitAddPlans"
          @delete-plan="deleteComponent"
          @save-data="saveData" />
      </template>
    </coozzy-card>
  </section>
</template>

<script>
import { routeChecks } from '@/mixins/routeChecks'
import { tables } from '@/mixins/tables'
import { user } from '@/mixins/user'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import { componentUtils } from '@/mixins/componentUtils'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyFilterIcon from '@/framework/components/icons/CoozzyFilterIcon.vue'
import { helper } from '@/types/helper'
import ObjectApi from '@/misc/apis/ObjectApi'
import Vue from 'vue'
import InvestPlanApi from '@/misc/apis/InvestPlanApi'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import ComponentCosts from '@/property/tabs/ComponentCosts.vue'
import PlanningCosts from '@/property/tabs/PlanningCosts.vue'

export default {
  name: 'InvestPlanTab',
  components: {
    PlanningCosts,
    ComponentCosts,
    CoozzyFormRadio,
    CoozzyFilterIcon,
    CoozzyButton,
    CoozzyCard
  },
  mixins: [routeChecks, tables, user, componentUtils, dateUtils, helper],
  props: {
    property: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    addNewComponent: {
      type: Boolean,
      default: false
    },
    investPlanFilters: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isBusy: true,
      defaultComponents: [],
      listComponents: [],
      listPlanningCosts: [],
      buildingsList: [],
      objectsList: [],
      selectedView: 'componentsCostsList',
      isFiltered: false,
      filters: {}
    }
  },
  computed: {
    componentsCount() {
      return this.listComponents !== null ? this.listComponents.length : 0
    },
    plansCount() {
      return this.listPlanningCosts !== null ? this.listPlanningCosts.length : 0
    }
  },
  watch: {
    currentPage: function () {
      this.handleCheckboxesInsideTable(false)
      this.$refs.header_checkbox.checked = false
    },
    nbrPerPage: function (newVal) {
      localStorage.setItem('investmentPlanningTab-nbrPerPage', newVal)
    },
    selectedView: {
      handler: function (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          const query = {}
          query.view = newVal
          this.$emit('change-filter', JSON.stringify(query), 'investPlanFilters')
        }
      }
    }
  },
  mounted: function () {
    this.loadData()
    const parsedFilters = this.investPlanFilters !== '' ? JSON.parse(this.investPlanFilters) : null
    if (parsedFilters) {
      this.filters = parsedFilters
      if (parsedFilters.view === 'componentsCostsList') {
        this.selectedView = 'componentsCostsList'
        if (this.addNewComponent === true && !this.readOnly) {
          this.$nextTick(() => {
            this.$refs.propertyComponentCosts.showCreateComponentModalClicked()
          })
        }
      } else if (parsedFilters.view === 'planningCostsList') {
        this.selectedView = 'planningCostsList'
        if (this.addNewComponent === true && !this.readOnly) {
          this.$nextTick(() => {
            this.$refs.propertyPlanningCosts.showCreatePlanModalClicked()
          })
        }
      }
    }
  },
  methods: {
    filtersChanged(value, query) {
      this.isFiltered = value
      query.view = this.selectedView
      this.$emit('change-filter', JSON.stringify(query), 'investPlanFilters')
    },
    goToEditComponent() {
      this.$router.push({
        name: this.isOwnerModule ? 'OwnerPropertyEditingView' : this.isAssetModule ? 'AssetPropertyEditingView' : this.isAccountingModule ? 'AccountingPropertyEditingView' : 'AdminPropertyEditingView',
        params: { id: this.property.id },
        query: {
          view: 'investmentPlanning',
          investPlanFilters: JSON.stringify({ view: this.selectedView }),
          addNewComponent: true
        }
      })
    },
    initFilterComponents() {
      this.$refs.propertyComponentCosts?.initFilter()
    },
    initFilterPlans() {
      this.$refs.propertyPlanningCosts?.initFilter()
    },
    dateToObject(item) {
      const componentIndex = this.listComponents.findIndex(component => component.id && item.id ? component.id === item.id : component.internalId === item.internalId)
      if (componentIndex !== -1) {
        this.listComponents[componentIndex].installationDate = this.toObject(this.listComponents[componentIndex].installationDateString)
        this.listComponents[componentIndex].plannedReplacementDate = this.toObject(this.listComponents[componentIndex].plannedReplacementDateString)
        // this.calculateReplacementDate(item)
      }
    },
    async loadData() {
      this.isBusy = true
      await ObjectApi.getBuildingsListByIds(this.property.buildingIds).then(response => {
        if (response.buildings) {
          this.buildingsList = response.buildings
        }
      })
      let objectIds = []
      this.buildingsList.forEach(building => {
        objectIds = objectIds.concat(building.objectIds)
      })
      await ObjectApi.listObjectsByIds(objectIds).then(response => {
        const list = response.objects
        // sort the list by category translated
        this.objectsList = list.sort((a, b) => {
          return this.$t('message.onBoarding.buildings.objects.category.' + a.category.toLowerCase()) > this.$t('message.onBoarding.buildings.objects.category.' + b.category.toLowerCase()) ? 1 : -1
        })
      })
      await this.loadComponents()
    },
    async loadComponents(firstLoading = true) {
      let refIds = []
      if (this.property) {
        refIds.push(this.property.id)
        if (this.property.buildingIds.length > 0) {
          refIds = refIds.concat(this.property.buildingIds)
        }
      }
      this.buildingsList.forEach(building => {
        refIds = refIds.concat(building.objectIds)
      })
      try {
        const response = await InvestPlanApi.listComponentsByReferenceIds(refIds)
        const componentsResponse = response.components
        const plansResponse = response.plans
        let components = []
        if (componentsResponse.length === 0 || (firstLoading && !componentsResponse.some(component => component.referenceId.includes('prop_')))) {
          components = await this.getDefaultComponents()
        }
        components = JSON.parse(JSON.stringify(componentsResponse.concat(components)))
        const order = ['prop_', 'building_', 'obj_']
        const getOrderIndex = (id) => order.findIndex(prefix => id.startsWith(prefix))

        const list = this.sortComponentsPlans(components)
        // Now merge back the sorted objects with the sorted properties and buildings
        this.listComponents = list.sort((a, b) => {
          const isOptionalA = a.optionalValueEnhancingComponent
          const isOptionalB = b.optionalValueEnhancingComponent

          // First, sort by optionalValueEnhancingComponent and referenceId
          if (isOptionalA && isOptionalB) {
            return getOrderIndex(a.referenceId) - getOrderIndex(b.referenceId)
          }

          if (isOptionalA) {
            return 1
          }
          if (isOptionalB) {
            return -1
          }

          // Then, apply existing sorting based on referenceId
          return getOrderIndex(a.referenceId) - getOrderIndex(b.referenceId)
        })
        this.listComponents.forEach(component => {
          component.installationDateString = this.$options.filters.objectToDateInput(component.installationDate)
          component.plannedReplacementDateString = this.$options.filters.objectToDateInput(component.plannedReplacementDate)
          component.calculatedReplacement = this.fillOutCalculatedReplacement(component)
          if (component.installationDate) {
            component.lifeSpan = this.fillOutLifeSpan(component)
          }
        })
        const list2 = this.sortComponentsPlans(plansResponse)
        list2.forEach(plan => {
          plan.dateString = this.$options.filters.objectToDateInput(plan.date)
        })
        this.listPlanningCosts = list2
      } catch (e) {
        console.log(e)
      }

      this.isBusy = false
    },
    sortComponentsPlans(items) {
      // Group components by referenceId
      const groupedComponents = items.reduce((acc, component) => {
        const key = component.referenceId
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(component)
        return acc
      }, {})

      // Sort each group by bkp
      for (const key in groupedComponents) {
        groupedComponents[key].sort((a, b) => {
          if (a.bkp === '' && b.bkp !== '') return 1
          if (a.bkp !== '' && b.bkp === '') return -1
          return a.bkp.localeCompare(b.bkp)
        })
      }

      // Separate objects from properties and buildings
      const objects = Object.values(groupedComponents).flat().filter(component => component.referenceId.startsWith('obj_'))
      const properties = Object.values(groupedComponents).flat().filter(component => component.referenceId.startsWith('prop_'))
      const buildings = Object.values(groupedComponents).flat().filter(component => component.referenceId.startsWith('building_'))

      // Sort the objects by number (numeric sorting)
      objects.sort((a, b) => {
        const objA = this.objectsList.find(obj => obj.id === a.referenceId)
        const objB = this.objectsList.find(obj => obj.id === b.referenceId)

        const numA = parseInt(objA.number)
        const numB = parseInt(objB.number)

        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB
        } else if (!isNaN(numA)) {
          return -1
        } else if (!isNaN(numB)) {
          return 1
        } else {
          return objA.number.localeCompare(objB.number)
        }
      })
      return [...properties, ...buildings, ...objects]
    },
    async getDefaultComponents() {
      const ownerId = this.property.ownerId
      const parkingSpaceExists = this.objectsList.some(obj => obj.category === 'PARKING_SPACE')
      this.typeComponentsProperty.forEach(type => {
        if (type !== 'COMPONENT_TYPE_UNDERGROUND_GARAGE' || (type === 'COMPONENT_TYPE_UNDERGROUND_GARAGE' && parkingSpaceExists)) {
          const internalId = Date.now() + Math.floor(Math.random() * 1000)
          const component = JSON.parse(JSON.stringify(helper.emptyComponent))
          component.internalId = JSON.parse(JSON.stringify('p_' + internalId))
          component.ownerId = ownerId
          component.referenceId = this.property.id
          component.type = type
          component.name = this.$t('message.onBoarding.investmentPlanning.types.' + type)
          component.bkp = this.getBkpComponent(type)
          component.lifeSpan = this.getLifeSpanComponent(type)
          component.percentage = this.getPercentageComponent(type)
          component.installationDateString = ''
          component.plannedReplacementDateString = ''
          component.calculatedReplacement = ''
          component.optionalValueEnhancingComponent = false
          this.defaultComponents.push(component)
        }
      })
      return this.defaultComponents
    },
    fillOutCalculatedReplacement(item) {
      if (item.installationDateString) {
        let date = new Date(item.installationDateString)
        return date.getFullYear() + (item.lifeSpan ? parseInt(item.lifeSpan) : 0)
      }
      return ''
    },
    fillOutLifeSpan(item) {
      if (item.calculatedReplacement) {
        let date = new Date(item.installationDateString)
        return item.calculatedReplacement - date.getFullYear()
      }
      return ''
    },
    createComponentClicked() {
      if (this.selectedView === 'componentsCostsList') {
        this.$refs.propertyComponentCosts.showCreateComponentModalClicked()
      } else if (this.selectedView === 'planningCostsList') {
        this.$refs.propertyPlanningCosts.showCreatePlanModalClicked()
      }
    },
    emitChangesComponents(components) {
      if (Array.isArray(components)) {
        components.forEach(component => {
          const index = this.listComponents.findIndex(c => c.id && component.id ? c.id === component.id : c.internalId === component.internalId)
          if (index !== -1) {
            this.$set(this.listComponents, index, component)
          } else {
            this.listComponents = [...this.listComponents, component]
          }
        })
        this.$emit('change-component', this.listComponents)
        return
      }
      const component = components
      const index = this.listComponents.findIndex(c => c.id && component.id ? c.id === component.id : c.internalId === component.internalId)
      if (index !== -1) {
        this.$set(this.listComponents, index, component)
      } else {
        this.listComponents = [...this.listComponents, component]
      }
      this.$emit('change-component', component)
    },
    emitAddComponents(refs, optionalValue = false) {
      refs.forEach(ref => {
        if (ref.includes('prop')) {
          this.addNewPropertyComponent(optionalValue)
        } else if (ref.includes('building')) {
          this.addNewBuildingComponent(ref, optionalValue)
        } else {
          this.addNewObjectComponent(ref, optionalValue)
        }
      })
    },
    addNewPropertyComponent(optionalValue = false) {
      const list = JSON.parse(JSON.stringify(this.listComponents))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const component = JSON.parse(JSON.stringify(helper.emptyComponent))
      component.internalId = JSON.parse(JSON.stringify(internalId))
      component.ownerId = this.property.ownerId
      component.referenceId = this.property.id
      component.optionalValueEnhancingComponent = optionalValue

      if (optionalValue) {
        list.push(component)
      } else {
        list.unshift(component)
      }
      this.$set(this, 'listComponents', list)
      this.$emit('change-component', this.listComponents)
    },
    addNewBuildingComponent(buildingId, optionalValue = false) {
      const list = JSON.parse(JSON.stringify(this.listComponents))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const component = JSON.parse(JSON.stringify(helper.emptyComponent))
      component.internalId = JSON.parse(JSON.stringify(internalId))
      component.ownerId = this.buildingsList.find(b => b.id === buildingId).ownerId
      component.referenceId = buildingId
      component.optionalValueEnhancingComponent = optionalValue

      if (optionalValue) {
        list.push(component)
      } else {
        list.unshift(component)
      }
      this.$set(this, 'listComponents', list)
      this.$emit('change-component', this.listComponents)
    },
    addNewObjectComponent(objectId, optionalValue = false) {
      const list = JSON.parse(JSON.stringify(this.listComponents))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const component = JSON.parse(JSON.stringify(helper.emptyComponent))
      component.internalId = JSON.parse(JSON.stringify(internalId))
      component.ownerId = this.objectsList.find(b => b.id === objectId).ownerId
      component.referenceId = objectId
      component.optionalValueEnhancingComponent = optionalValue

      if (optionalValue) {
        list.push(component)
      } else {
        list.unshift(component)
      }
      this.$set(this, 'listComponents', list)
      this.$emit('change-component', this.listComponents)
    },
    emitAddPlans(refs) {
      refs.forEach(ref => {
        if (ref.includes('prop')) {
          this.addNewPropertyPlan()
        } else if (ref.includes('building')) {
          this.addNewBuildingPlan(ref)
        } else {
          this.addNewObjectPlan(ref)
        }
      })
    },
    addNewPropertyPlan() {
      const list = JSON.parse(JSON.stringify(this.listPlanningCosts))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const plan = JSON.parse(JSON.stringify(helper.emptyPlan))
      plan.internalId = JSON.parse(JSON.stringify(internalId))
      plan.ownerId = this.property.ownerId
      plan.referenceId = this.property.id

      list.unshift(plan)

      this.$set(this, 'listPlanningCosts', list)
      this.$emit('change-component', this.listPlanningCosts)
    },
    addNewBuildingPlan(buildingId) {
      const list = JSON.parse(JSON.stringify(this.listPlanningCosts))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const plan = JSON.parse(JSON.stringify(helper.emptyPlan))
      plan.internalId = JSON.parse(JSON.stringify(internalId))
      plan.ownerId = this.buildingsList.find(b => b.id === buildingId).ownerId
      plan.referenceId = buildingId

      list.unshift(plan)

      this.$set(this, 'listPlanningCosts', list)
      this.$emit('change-component', this.listPlanningCosts)
    },
    addNewObjectPlan(objectId) {
      const list = JSON.parse(JSON.stringify(this.listPlanningCosts))
      const internalId = Date.now() + Math.floor(Math.random() * 1000)
      const plan = JSON.parse(JSON.stringify(helper.emptyPlan))
      plan.internalId = JSON.parse(JSON.stringify(internalId))
      plan.ownerId = this.objectsList.find(b => b.id === objectId).ownerId
      plan.referenceId = objectId

      list.unshift(plan)

      this.$set(this, 'listPlanningCosts', list)
      this.$emit('change-component', this.listPlanningCosts)
    },
    emitChangesPlans(plans) {
      if (plans.length > 0) {
        plans.forEach(plan => {
          const index = this.listPlanningCosts.findIndex(c => c.id && plan.id ? c.id === plan.id : c.internalId === plan.internalId)
          if (index !== -1) {
            this.listPlanningCosts[index] = plan
            this.listComponents[index].modified = true
          } else {
            this.listPlanningCosts.push(plan)
          }
        })
        this.$emit('change-component', this.listPlanningCosts)
      }
    },
    deleteNewComponent(internalIds) {
      this.listComponents = this.listComponents.filter(component => !internalIds.includes(component.internalId))
    },
    async saveData() {
      this.isBusy = true
      const promises = []
      const listComponentToCreate = []
      const listComponentToEdit = []
      const listPlanToCreate = []
      const listPlanToUpdate = []
      for (const component of this.listComponents) {
        if (component.internalId) {
          listComponentToCreate.push(component)
        } else if (component.modified) {
          listComponentToEdit.push(component)
        }
      }
      for (const plan of this.listPlanningCosts) {
        if (plan.internalId) {
          listPlanToCreate.push(plan)
        } else if (plan.modified) {
          listPlanToUpdate.push(plan)
        }
      }

      if (listComponentToCreate.length > 0 || listPlanToCreate.length > 0) {
        promises.push(InvestPlanApi.createComponentBulk(listComponentToCreate, listPlanToCreate))
      }
      if (listComponentToEdit.length > 0 || listPlanToUpdate.length > 0) {
        promises.push(InvestPlanApi.updateComponentBulk(listComponentToEdit, listPlanToUpdate))
      }
      return Promise.all(promises).then(() => {
        Vue.toasted.show(this.$tc('message.successMessages.componentSaved'), { type: 'success' })
        this.loadComponents(false)
      })
    },
    deleteComponent(ids) {
      this.isBusy = true
      const promises = []
      ids.forEach((id) => {
        promises.push(InvestPlanApi.deleteComponent(id))
      })
      return Promise.all(promises).then(() => {
        Vue.toasted.show(this.$tc('message.successMessages.componentDeleted'), { type: 'success' })
        this.loadComponents(false)
      }).catch(e => {
        console.error(e)
        Vue.toasted.show(this.$tc('message.savingErrors.data'), { type: 'error' })
      }).finally(() => {
        this.isBusy = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.custom-radio.likeBtn) {
  input {
    display: none;
  }

  label:before {
    display: none;
  }

  label {
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    line-height: 16px;
    height: 31px;
    width: 100%;
    box-shadow: none !important;
    color: $color-text-grey-dark !important;
    background-color: $color-bg-grey-dark !important;
    border-color: $color-bg-grey-dark !important;
    display: inline-block;

    ::after {
      display: none;
    }
  }

  input:checked + label {
    color: $color-text-white !important;
    background-color: $color-primary !important;
    border-color: $color-primary !important;
  }

  input:disabled + label {
    opacity: .65;
  }
}

:deep(.custom-radio.btn .custom-control-label::after) {
  display: none;
}

.global-card-tab > :deep(.card-body) {
  padding: 0;
}
</style>
